import React, { useContext, useEffect, useState } from 'react';
import { Spinner, useToast } from '@chakra-ui/react';

import { deletePaymentForLeaseVeh, getDriversInfo, getLeasedVehPaymentStats } from '../../APIs'
import PATHS from "../../Routes/paths"

import { ClientPicker, DatePicker, ReactTable } from '../../Components/Molecules'
import {Breadcrumb, CardsGroup, PopUpModal} from '../../Components/Atoms'
import ImageViewer from '../../Components/Atoms/ImageViewer'
import AddPayment from './addPaymentModal'

import { VEHICLES_PAYMENT_HEADERS } from '../../Library/tableHeaders'
import { StoreContext } from '../../Library/contexts'
import { DATE_RANGES, PAYMENT_MODES } from '../../Library/constants'
import { getFilters } from '../../Library/tableHelpers'
import { getISODate } from '../../Library/datetime';
import { getFomattedAmt } from '../../Library/helpers'

import { DeleteIcon } from '../../Assets/images/svgs'
import "./style.css";
import { Link } from 'react-router-dom';

const paymentCards = [
  { idx: 1, key: "amt", heading: "Actual Amount" },
  { idx: 2, key: "rcvdAmt", heading: "Amount Received" },
  { idx: 3, key: "outsdAmt", heading: "Amount Outstanding" },
];

const Payments = () => {
  const [isAddPaymentModalVisible, setIsAddPaymentModalVisible] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  const [showPaymentDelConfirmModal, setShowPaymentDelConfirmModal] = useState(null);

  const [clientDrivers, setClientDrivers] = useState([]);

  const { filters, constants, setConstants } = useContext(StoreContext);

  const toast = useToast();
  
  const paths = [
    { idx: 0, name: "Lease", link: PATHS.LEASE },
    { idx: 1, name: "Payments", isCurrentPage: true },
  ];
  
  const colorArr = ["#409BFF", "#2AC670", "#BD4620"];

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      const response = await getLeasedVehPaymentStats(filters.client, range);
      if (!response || !response.length) {
        setPaymentData([ ]);
        return;
      }

      let parsedPaymentMode = response.map((el) => {
        el.pmtMode = PAYMENT_MODES[el.pmtMode]
        return el;
      });

      setPaymentData(parsedPaymentMode);
    };

    let range;
    if (DATE_RANGES[filters.range]) {
      range = DATE_RANGES[filters.range];
    }

    fetchPaymentHistory();


    const fetchDrivers = (clientId) => {
      getDriversInfo(clientId, null, null, true, (err, resp) => {
        if (err) console.error(err);
        else {
          resp.forEach(driver => {
            driver.label = driver.dNm;
            driver.value = driver.idx;
          })
          setClientDrivers(resp);
        }
      })
    }

    fetchDrivers(filters.client);
  }, [ filters ]);

  const hanldePaymentDelete = (value) => {
    setShowPaymentDelConfirmModal({
      open: true,
      modalMsg: "Are you sure you want to delete this payment ?",
      closeBtn: "Cancel",
      confirmBtn: "Yes",
      onConfirm: () => onPaymentDeleted(value),
      onModalClose: () => setShowPaymentDelConfirmModal(null),
    })
  }

  const getCellData = (header, row) => {
    if(header.key === "ssUrl" && row.value){
      return <ImageViewer imgUrl={row.value} isDownloadable={true} imgName='screenshot.jpg'/>;
    }
    else if(header.delete){
      return <div className='payment-delete-icon' onClick={() => hanldePaymentDelete(row.value)}><DeleteIcon /></div>;
    }
    else if (header.key === "ts") {
      return getISODate(row.value).split('-').reverse().join('-');
    }
    else if (header.key === "drId") {
      let driver = clientDrivers.find(driver => driver.idx === row.value);
      if (driver && driver.dNm) {
        return <Link className="text-blue" to={`${PATHS.LEASE}/${PATHS.DRIVERS}/${PATHS.DRIVER_STATS}?idx=${driver.idx}`}>{ driver.dNm }</Link>;
      } else {
        return "-";
      }
    }
    else{
      return (row.value || "-");
    }
  }

  const columns = React.useMemo(() => Object.values(VEHICLES_PAYMENT_HEADERS).map(header => {
    return {
      Header: header.nm,
      accessor: header.key,
      disableSortBy: !header.sorting,
      ...getFilters(header),
      Cell: (row) => getCellData(header, row)
    };
  }),[paymentData]);

  const updateClientPaymentData = (amt = 0) => {
    setConstants((old) => {
      let oldClients = old.clientMap;
      let selClient = oldClients[filters.client];

      selClient.outsdAmt -= amt;
      selClient.rcvdAmt += amt;

      return { ...old };
    });
  };

  const onPaymentDeleted = (idx) => {
    const index = paymentData.findIndex((payment) => payment.idx === idx);
    const deletedAmt = paymentData[index].amt;

    setPaymentData((old) => {
      old.splice(index, 1);
      return [...old];
    })

    deletePaymentForLeaseVeh(idx)
    .then(() => {
      toast({
        title: 'Payment successfully deleted',
        description: "Payment successfully deleted",
        status: 'success',
        duration: 9000,
        isClosable: true,
      })
      updateClientPaymentData(-deletedAmt);
    })
    .catch(() => {
      toast({
        title: 'Payment deletion failed.',
        description: "Something went wrong",
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
    })

    setShowPaymentDelConfirmModal(null);
  }

  const onPaymentAdded = (payment) => {
    payment.pmtMode = PAYMENT_MODES[payment.pmtMode]
    setPaymentData((old) => ([ payment, ...old ]));

    updateClientPaymentData(payment.amt);
  };

  const closeHandler = () => {
    setIsAddPaymentModalVisible(false);
  }

  const generateCardGroup = (payments) => {
    return payments.map((payment) => ({
      idx: payment.idx,
      heading: payment.heading,
      desc: getFomattedAmt(constants.clientMap[filters.client][payment.key]),
    }));
  };

  return (
    <section  className="panel-main">
      <header className="panel-filters">
        <Breadcrumb paths={paths} />
        <div style={{ width: 150 }} />
        <ClientPicker />
        <DatePicker />
      </header>

      <article style={{paddingTop: 30}}>
        <CardsGroup data={generateCardGroup(paymentCards)} colors={colorArr} />

        <div style={{marginTop: 41}} className="payment-table">
          <AddPayment clientDrivers={clientDrivers} isOpen={isAddPaymentModalVisible} closeHandler = {closeHandler} handleAdd={onPaymentAdded} />

          {paymentData ? (
            <ReactTable
              data={paymentData}
              columns={columns}
              showOverlay={false}
              showSearch={true}
              heading={"Payments"}
              buttonName={"Add Payment"}
              buttonHandler={() => setIsAddPaymentModalVisible(true)}
            />) : (
            <div className="add-model-loader">
              <Spinner />
            </div>
          )}
        </div>
      </article>
      <PopUpModal { ...showPaymentDelConfirmModal } />
    </section>
  )
}

export default Payments
